/**
 * Matrix Filter Classification
 *
 * Provides classification logic for hexagon data visualization.
 * Supports two classification modes:
 *
 * 1. Matrix (2D) Classification:
 *    - Uses two properties: one for "bands" (rows) and one for "thresholds" (columns)
 *    - Each band has its own threshold boundaries
 *    - Example: Height bands + Density thresholds (Allskog style)
 *
 * 2. Simple (1D) Classification:
 *    - Uses a single property with min/max ranges per class
 *    - Example: Volume-based classification with Low/Medium/High classes
 *
 * @module matrixClassification
 */

/** Default label for the custom price/m² field */
export const DEFAULT_CUSTOM_PRICE_LABEL = 'Custom price/m²';

/**
 * Available hexagon properties that can be used for classification.
 * Each property maps to a field in hexagon metadata.
 */
export const AVAILABLE_PROPERTIES = [
	{ id: 'mean_height', label: 'Mean Height (m)', unit: 'm' },
	{ id: 'count', label: 'Count', unit: 'pcs' },
	{ id: 'density', label: 'Density (/dekar)', unit: '/dekar' },
	{ id: 'mean_crown_diameter', label: 'Crown Diameter (m)', unit: 'm' },
	{ id: 'total_area', label: 'Total Area (m²)', unit: 'm²' },
	{ id: 'confidence', label: 'Mean Confidence', unit: '%' },
	{ id: 'stems_low', label: 'Stem Estimate (Low Productivity)', unit: 'pcs' },
	{ id: 'stems_medium', label: 'Stem Estimate (Medium Productivity)', unit: 'pcs' },
	{ id: 'stems_high', label: 'Stem Estimate (High Productivity)', unit: 'pcs' },
	{ id: 'stems_per_ha_low', label: 'Stem Estimate/ha (Low)', unit: '/ha' },
	{ id: 'stems_per_ha_medium', label: 'Stem Estimate/ha (Medium)', unit: '/ha' },
	{ id: 'stems_per_ha_high', label: 'Stem Estimate/ha (High)', unit: '/ha' },
	{ id: 'hectare_equivalent', label: 'Hectare Equivalent', unit: '/ha' },
	// Volume estimation properties
	{ id: 'volume_m3', label: 'Volume (m³)', unit: 'm³' },
	{ id: 'volume_per_ha_m3', label: 'Volume/ha (m³/ha)', unit: 'm³/ha' },
	{ id: 'basal_area_m2', label: 'Basal Area (m²)', unit: 'm²' },
	{ id: 'estimated_stems', label: 'Estimated Stems', unit: 'pcs' },
];

/**
 * Classify a hexagon using a matrix filter
 * @param {object} hexagonMeta - Hexagon metadata object
 * @param {object} filter - Matrix filter configuration
 * @returns {string|null} - Class ID or null
 */
export function classifyWithMatrixFilter(hexagonMeta, filter) {
	if (!filter?.bands?.length || !filter?.thresholds?.length) {
		return null;
	}

	// Get values for both axes
	const bandValue = getPropertyValue(hexagonMeta, filter.bandProperty);
	const thresholdValue = getPropertyValue(hexagonMeta, filter.thresholdProperty);

	if (bandValue === null || thresholdValue === null) {
		return null;
	}

	// Find closest band
	let bandIndex = 0;
	let minDiff = Math.abs(bandValue - filter.bands[0]);

	for (let i = 1; i < filter.bands.length; i++) {
		const diff = Math.abs(bandValue - filter.bands[i]);
		if (diff < minDiff) {
			minDiff = diff;
			bandIndex = i;
		}
	}

	const threshold = filter.thresholds[bandIndex];
	if (!threshold) return null;

	// Check minimum value
	if (thresholdValue < threshold.min) {
		return 'below_minimum';
	}

	// Check maximum value (if defined)
	if (threshold.max !== undefined && thresholdValue > threshold.max) {
		return 'unclassified';
	}

	// Find class based on boundaries
	// Each class has a boundary that defines its upper limit
	for (let i = 0; i < filter.classes.length; i++) {
		const boundary = threshold.boundaries[i];
		// If boundary exists and value is within it, return this class
		if (boundary !== undefined && thresholdValue <= boundary) {
			return filter.classes[i]?.id || null;
		}
	}

	// Above all boundaries = unclassified
	return 'unclassified';
}

/**
 * Get property value from hexagon metadata
 * Supports computed properties like density
 */
export function getPropertyValue(meta, propertyId) {
	if (!meta || !propertyId) return null;

	switch (propertyId) {
		case 'mean_height':
			return meta.mean_height ?? null;
		case 'count':
			return meta.count ?? null;
		case 'density':
			// If density is pre-calculated, use it
			if (meta.density !== undefined) return meta.density;
			// Compute density from count and area (per-dekar: 1 dekar = 1000 m²)
			// Cannot infer density without area information
			if (meta.count !== undefined && meta.area !== undefined && meta.area > 0) {
				return (meta.count / meta.area) * 1000;
			}
			return null; // Cannot compute density without area
		case 'hectare_equivalent':
			// If hectare_equivalent is provided by the backend, use it
			if (meta.hectare_equivalent !== undefined) return meta.hectare_equivalent;
			// Compute from count and area: extrapolate detections to per-hectare
			if (meta.count !== undefined && meta.area !== undefined && meta.area > 0) {
				return (meta.count / meta.area) * 10000;
			}
			return null;
		case 'mean_crown_diameter':
			return meta.mean_crown_diameter ?? meta.crown_diameter ?? null;
		case 'total_area':
			return meta.total_area ?? meta.area ?? null;
		case 'confidence':
			return meta.confidence ?? meta.mean_confidence ?? null;
		case 'stems_low':
			return meta.stems_low ?? null;
		case 'stems_medium':
			return meta.stems_medium ?? null;
		case 'stems_high':
			return meta.stems_high ?? null;
		case 'stems_per_ha_medium':
			return meta.stems_per_ha_medium ?? null;
		case 'stems_per_ha_low':
			return meta.stems_per_ha_low ?? null;
		case 'stems_per_ha_high':
			return meta.stems_per_ha_high ?? null;
		// Volume estimation properties
		case 'volume_m3':
			return meta.volume_m3 ?? null;
		case 'volume_per_ha_m3':
			return meta.volume_per_ha_m3 ?? null;
		case 'basal_area_m2':
			return meta.basal_area_m2 ?? null;
		case 'estimated_stems':
			return meta.estimated_stems ?? null;
		default:
			return meta[propertyId] ?? null;
	}
}

/**
 * Get class info by ID
 */
export function getClassById(classId, filter) {
	if (classId === 'below_minimum') {
		return {
			id: 'below_minimum',
			label: 'Below Minimum',
			color: { r: 128, g: 128, b: 128, a: 0.5 },
		};
	}
	if (classId === 'unclassified') {
		return {
			id: 'unclassified',
			label: 'Unclassified',
			color: { r: 100, g: 100, b: 100, a: 0.3 },
		};
	}
	return filter.classes.find(c => c.id === classId) || null;
}

/**
 * Convert hex color string to RGBA object
 */
export function hexToRgba(hex, alpha = 0.7) {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: parseInt(result[1], 16),
				g: parseInt(result[2], 16),
				b: parseInt(result[3], 16),
				a: alpha,
			}
		: { r: 128, g: 128, b: 128, a: alpha };
}

/**
 * Convert RGBA object to hex string
 */
export function rgbaToHex(rgba) {
	const toHex = c => c.toString(16).padStart(2, '0');
	return `#${toHex(rgba.r)}${toHex(rgba.g)}${toHex(rgba.b)}`;
}

/**
 * Available currencies for pricing calculations.
 * First entry (UNIT) is used as fallback when no currency is specified.
 * @type {Array<{id: string, symbol: string, label: string}>}
 */
export const CURRENCIES = [
	{ id: 'UNIT', symbol: '', label: 'Units (no currency)' },
	{ id: 'NOK', symbol: 'kr', label: 'Norwegian Krone (NOK)' },
	{ id: 'EUR', symbol: '€', label: 'Euro (EUR)' },
	{ id: 'USD', symbol: '$', label: 'US Dollar (USD)' },
	{ id: 'SEK', symbol: 'kr', label: 'Swedish Krona (SEK)' },
	{ id: 'GBP', symbol: '£', label: 'British Pound (GBP)' },
];

/**
 * Create an empty matrix (2D) filter template.
 * Used as starting point for new matrix filters.
 * @returns {Object} Empty matrix filter configuration
 */
export function createEmptyMatrixFilter() {
	return {
		name: 'New Matrix Filter',
		type: 'matrix',
		bandProperty: 'mean_height',
		thresholdProperty: 'count',
		currency: 'EUR',
		customPriceFields: [],
		bands: [2, 3, 4, 5, 6],
		classes: [
			{ id: 'class_1', label: 'Easy', color: '#4ade80', opacity: 0.7, pricePerM2: 0 },
			{ id: 'class_2', label: 'Medium', color: '#fbbf24', opacity: 0.7, pricePerM2: 0 },
			{ id: 'class_3', label: 'Hard', color: '#ef4444', opacity: 0.7, pricePerM2: 0 },
		],
		thresholds: [
			{ min: 200, max: 1000, boundaries: [400, 700, 1000] },
			{ min: 200, max: 900, boundaries: [350, 600, 900] },
			{ min: 200, max: 800, boundaries: [300, 500, 800] },
			{ min: 200, max: 700, boundaries: [250, 450, 700] },
			{ min: 150, max: 600, boundaries: [200, 350, 600] },
		],
	};
}

/**
 * Default matrix (2D) filter preset with 5 difficulty classes.
 * Configured for height bands with density thresholds.
 * @type {Object}
 */
export const DEFAULT_PRESET = {
	name: 'Default Classification',
	type: 'matrix',
	bandProperty: 'mean_height',
	thresholdProperty: 'density',
	currency: 'EUR',
	customPriceFields: [],
	bands: [2, 3, 4, 5, 6],
	classes: [
		{ id: 'very_easy', label: 'Very Easy', color: '#90EE90', opacity: 0.7, pricePerM2: 0.5 },
		{ id: 'easy', label: 'Easy', color: '#FFFF00', opacity: 0.7, pricePerM2: 1.0 },
		{ id: 'medium', label: 'Medium', color: '#FFA500', opacity: 0.7, pricePerM2: 1.5 },
		{ id: 'hard', label: 'Hard', color: '#FF6347', opacity: 0.7, pricePerM2: 2.0 },
		{ id: 'very_hard', label: 'Very Hard', color: '#FF0000', opacity: 0.7, pricePerM2: 3.0 },
	],
	thresholds: [
		{ min: 250, max: 3000, boundaries: [600, 1000, 1500, 2000, 3000] },
		{ min: 250, max: 2500, boundaries: [500, 800, 1200, 1600, 2500] },
		{ min: 200, max: 2000, boundaries: [400, 650, 1000, 1300, 2000] },
		{ min: 200, max: 1500, boundaries: [350, 550, 850, 1100, 1500] },
		{ min: 150, max: 1200, boundaries: [300, 450, 700, 950, 1200] },
	],
};

/**
 * Create a simple (1D) filter template.
 * Uses a single property with min/max ranges per class.
 * @param {string} property - Property ID to classify by (default: 'volume_m3')
 * @returns {Object} Simple filter configuration
 */
export function createSimpleFilter(property = 'volume_m3') {
	return {
		name: 'New Simple Filter',
		type: 'simple',
		property: property,
		currency: 'EUR',
		customPriceFields: [],
		classes: [
			{
				id: 'class_1',
				label: 'Low',
				color: '#4ade80',
				opacity: 0.7,
				pricePerM2: 0,
				min: 0,
				max: 5,
			},
			{
				id: 'class_2',
				label: 'Medium',
				color: '#fbbf24',
				opacity: 0.7,
				pricePerM2: 0,
				min: 5,
				max: 10,
			},
			{
				id: 'class_3',
				label: 'High',
				color: '#ef4444',
				opacity: 0.7,
				pricePerM2: 0,
				min: 10,
				max: null,
			},
		],
	};
}

/**
 * Default simple (1D) filter preset for volume classification.
 * 5 classes from Very Low to Very High based on volume_m3.
 * @type {Object}
 */
export const DEFAULT_SIMPLE_PRESET = {
	name: 'Volume Classification',
	type: 'simple',
	property: 'volume_m3',
	currency: 'EUR',
	customPriceFields: [],
	classes: [
		{
			id: 'very_low',
			label: 'Very Low',
			color: '#90EE90',
			opacity: 0.7,
			pricePerM2: 0,
			min: 0,
			max: 2,
		},
		{ id: 'low', label: 'Low', color: '#FFFF00', opacity: 0.7, pricePerM2: 0, min: 2, max: 5 },
		{
			id: 'medium',
			label: 'Medium',
			color: '#FFA500',
			opacity: 0.7,
			pricePerM2: 0,
			min: 5,
			max: 10,
		},
		{
			id: 'high',
			label: 'High',
			color: '#FF6347',
			opacity: 0.7,
			pricePerM2: 0,
			min: 10,
			max: 20,
		},
		{
			id: 'very_high',
			label: 'Very High',
			color: '#FF0000',
			opacity: 0.7,
			pricePerM2: 0,
			min: 20,
			max: null,
		},
	],
};

/**
 * Classify a hexagon using a simple (1D) filter
 * @param {object} hexagonMeta - Hexagon metadata object
 * @param {object} filter - Simple filter configuration
 * @returns {string|null} - Class ID or null
 */
export function classifyWithSimpleFilter(hexagonMeta, filter) {
	if (!filter?.classes?.length || !filter?.property) {
		return null;
	}

	const value = getPropertyValue(hexagonMeta, filter.property);
	if (value === null) {
		return 'unclassified';
	}

	// Find class based on min/max ranges
	for (const cls of filter.classes) {
		const min = cls.min ?? 0;
		const max = cls.max ?? Infinity;

		if (value >= min && (cls.max === null || value <= max)) {
			return cls.id;
		}
	}

	return 'unclassified';
}
